import { useMutation, useQuery } from '@apollo/client'
import debounce from 'lodash/debounce'
import { FC, useCallback } from 'react'
import { DEBOUNCE_DELAY } from 'constants/consts'
import { createTmLinkVariables, createTmLink } from '__generatedTypes__/createTmLink'
import {
  searchTeamMember,
  searchTeamMember_searchTeamMember_items as SearchedTeamMember,
  searchTeamMemberVariables,
} from '__generatedTypes__/searchTeamMember'
import { Autosuggest } from 'components/Autosuggest/Autosuggest'
import { CREATE_TEAM_MEMBERS_LINK, SEARCH_TEAM_MEMBER } from 'queries/teamMember'
import { toast } from 'utils/toast'
import TeamMemberRenderItem from './TeamMemberRenderItem'

interface IProps {
  platformId: string
  teamMembersIndexedLength: number
  refetch: () => void
  onEditClick: (member: SearchedTeamMember) => void
  addedTeamMemberIds: string[]
}

const TeamMembersSearch: FC<IProps> = ({
  platformId,
  teamMembersIndexedLength,
  refetch,
  onEditClick,
  addedTeamMemberIds,
}) => {
  const excludeIds = addedTeamMemberIds?.length ? addedTeamMemberIds : undefined

  const [createTmLink] = useMutation<createTmLink, createTmLinkVariables>(CREATE_TEAM_MEMBERS_LINK)
  const teamMembersState = useQuery<searchTeamMember, searchTeamMemberVariables>(SEARCH_TEAM_MEMBER, {
    variables: {
      excludeIds,
    },
  })

  const handleTeamMemberInputChange = useCallback(
    debounce((searchString: string) => {
      teamMembersState.refetch({
        searchString,
        excludeIds,
      })
    }, DEBOUNCE_DELAY),
    [excludeIds]
  )

  const handleTeamMemberLinkCreate = useCallback(
    async (member: SearchedTeamMember) => {
      try {
        await createTmLink({
          variables: {
            createTmLinkInput: {
              platform: { id: platformId },
              order: teamMembersIndexedLength,
              teamMember: { id: member.id },
            },
          },
        })
        refetch()
        toast.success('Team member added successfully')
      } catch (error) {
        toast.error('Failed to add team member')
      }
    },
    [teamMembersIndexedLength, platformId]
  )

  const teamMembers = teamMembersState.data?.searchTeamMember.items ?? []

  return (
    <Autosuggest<SearchedTeamMember>
      items={teamMembers}
      onInputChange={handleTeamMemberInputChange}
      getItemLabel={(member) => member.fullName}
      renderItem={(member, _, { close }) => (
        <TeamMemberRenderItem
          member={member}
          onAddClick={handleTeamMemberLinkCreate}
          onEditClick={(editedMember) => {
            close()
            onEditClick(editedMember)
          }}
        />
      )}
      placeholder="Search User"
      searchable
      withoutCheckMark
      fullWidth
      onChange={() => undefined}
      value={[]}
    />
  )
}

export default TeamMembersSearch
